import React, { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";
import { GOLD } from "../constants/theme";
import { HINTS } from "../utils/community";

export function SearchBar({ C, allItems = [], onNavigate }) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [hIdx, setHIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const inputRef = useRef(null);

  // Typing animation for placeholder when not focused. Pauses while the tab
  // is hidden — this otherwise re-renders the topbar every 48ms forever,
  // even with the app backgrounded.
  useEffect(() => {
    if (focused) return;
    const hint = HINTS[hIdx]; let i = 0; setTyped("");
    let cycleT = null;
    const iv = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      i++; setTyped(hint.slice(0, i));
      if (i >= hint.length) { clearInterval(iv); cycleT = setTimeout(() => setHIdx(x => (x + 1) % HINTS.length), 2000); }
    }, 48);
    return () => { clearInterval(iv); if (cycleT) clearTimeout(cycleT); };
  }, [hIdx, focused]);

  // Search the whole drill, not just its name. This used to match title,
  // category and `tag` — but mapRow sets `tag = category`, so it was really
  // two fields, and a coach searching a word from a drill's description or
  // its tags got nothing back. desc, tags[] and keyPoints[] are all already
  // on the object; they just weren't being read.
  const results = (() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const hit = (v) => typeof v === "string" && v.toLowerCase().includes(q);
    const hitAny = (arr) => Array.isArray(arr) && arr.some(hit);
    const scored = [];
    for (const i of allItems) {
      // Title matches rank above body matches, so an exact drill name still
      // comes first now that the net is wider.
      if (hit(i.title)) scored.push([0, i]);
      else if (hit(i.category) || hitAny(i.tags)) scored.push([1, i]);
      else if (hit(i.desc) || hitAny(i.keyPoints)) scored.push([2, i]);
      if (scored.length >= 200) break;
    }
    return scored.sort((a, b) => a[0] - b[0]).slice(0, 8).map(([, i]) => i);
  })();

  const handleSelect = (item) => {
    setQuery("");
    setFocused(false);
    if (item.qa) {
      try { sessionStorage.setItem("bam_open_qa", JSON.stringify(item.qa)); } catch {}
      if (onNavigate) onNavigate("resources");
      return;
    }
    // Land on the ITEM, not just its section — ContentPage consumes this
    // stash once its drill list hydrates (same path dashboard bookmarks
    // use). Without it, clicking an exact search result dropped you at
    // the top of the section to go find it again.
    try { sessionStorage.setItem("bam_open_drill", JSON.stringify({ sectionId: item.sectionId || "pd", id: item.id })); } catch {}
    if (onNavigate) onNavigate(item.sectionId || "pd");
  };

  return (
    <div style={{ position: "relative", width: 280 }}>
      <div
        onClick={() => { setFocused(true); inputRef.current?.focus(); }}
        style={{
          background: focused ? "rgba(226,221,159,0.06)" : "rgba(255,255,255,0.03)",
          border: `1px solid ${focused ? GOLD + "50" : C.cardBorder || C.border}`,
          borderRadius: 10, padding: "8px 14px",
          display: "flex", alignItems: "center", gap: 9,
          cursor: "text", transition: "all .2s",
        }}>
        <Search size={13} color={GOLD} />
        {focused ? (
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onBlur={() => setTimeout(() => setFocused(false), 200)}
            autoFocus
            placeholder="Search drills, coaches, insights..."
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              fontSize: 13, color: C.text, fontFamily: "inherit",
            }}
          />
        ) : (
          // `translate="no"` + `notranslate` class disable Chrome auto-
          // translate on this animated hint. When the typing animation
          // updates state every 48ms, an active translation extension
          // wraps the text node in a <font> tag mid-cycle and React's
          // next diff hits "removeChild: not a child" (Sentry b7211bb).
          // Wrapping {typed} in its own <span> also gives React a stable
          // element to swap instead of a bare text-node sibling, which
          // is more resilient to any other DOM mutators (Grammarly etc).
          <span translate="no" className="notranslate"
            style={{ fontSize: 13, color: C.textDim, flex: 1, overflow: "hidden", whiteSpace: "nowrap" }}>
            <span>{typed}</span>
            <span style={{ borderRight: `2px solid ${GOLD}`, marginLeft: 1, display: "inline-block", height: "0.85em", verticalAlign: "text-bottom", animation: "blink 1s infinite" }} />
          </span>
        )}
        {query && focused && (
          <div onClick={(e) => { e.stopPropagation(); setQuery(""); }} style={{ cursor: "pointer", padding: 2 }}>
            <X size={12} color={C.textDim} />
          </div>
        )}
      </div>

      {/* Results dropdown */}
      {focused && results.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200,
          background: C.bgCard || "#151515", border: `1px solid ${GOLD}40`,
          borderRadius: 12, marginTop: 4, maxHeight: 320, overflow: "auto",
          boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
        }}>
          <div style={{ padding: "8px 12px", fontSize: 10, fontWeight: 800, color: C.textDim, letterSpacing: .8, textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>
            {results.length} result{results.length !== 1 ? "s" : ""}
          </div>
          {results.map((item) => (
            <div
              key={`${item.sectionId}-${item.id}`}
              onMouseDown={() => handleSelect(item)}
              style={{
                padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
                borderBottom: `1px solid ${C.border}`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD}10`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
              {item.muxPlaybackId && (
                <img
                  src={`https://image.mux.com/${item.muxPlaybackId}/thumbnail.jpg?width=80&height=45&fit_mode=crop`}
                  alt=""
                  style={{ width: 48, height: 32, borderRadius: 4, objectFit: "cover", flexShrink: 0 }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</div>
                <div style={{ fontSize: 10, color: C.textDim, marginTop: 1 }}>
                  {item.category || item.tag} · {item.duration}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {focused && query.trim().length > 1 && results.length === 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200,
          background: C.bgCard || "#151515", border: `1px solid ${C.border}`,
          borderRadius: 12, marginTop: 4, padding: "16px 14px", textAlign: "center",
          boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
        }}>
          <div style={{ fontSize: 13, color: C.textDim }}>No results for "{query}"</div>
        </div>
      )}
    </div>
  );
}
